import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useYouTubePlayer from '../../../hooks/useYouTubePlayer';
import { extractYouTubeVideoId, youTubeEmbedUrl, youTubeThumbnailUrl } from '../../../utils/youtube';
import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as RepeatIcon } from '../../../icons/repeat.svg';

import './YouTubePreview.scss';

/**
 * The video's thumbnail with Play and Practice on it, so a swipe over it reaches the page rather
 * than YouTube's iframe. The player is loaded underneath from the start: Play then starts it inside
 * the tap, which iOS needs before it will play sound, instead of loading one that has to be tapped again.
 */
const YouTubePreview = ({ url, onPractice, className }) => {
  const { t } = useTranslation();
  const videoId = extractYouTubeVideoId(url);
  const { mountRef, ready, failed, play } = useYouTubePlayer(videoId);
  // Keyed to the video, so a new link goes back to its thumbnail
  const [revealedId, setRevealedId] = useState(null);
  const [pendingPlayId, setPendingPlayId] = useState(null);
  const revealed = revealedId === videoId;

  // A tap before the player was ready plays once it is, though iOS may then want a second tap
  useEffect(() => {
    if (!ready || pendingPlayId !== videoId) return;
    play();
    setPendingPlayId(null);
  }, [ready, pendingPlayId, videoId, play]);

  if (!videoId) return null;

  const handlePlay = () => {
    setRevealedId(videoId);
    if (ready) play();
    else setPendingPlayId(videoId);
  };

  return (
    <div className={`YouTubePreview ${className || ''}`}>
      {failed ? (
        <iframe
          className="player"
          src={youTubeEmbedUrl(videoId)}
          title={t('songForm.youtubePreview')}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <div className="player" ref={mountRef} />
      )}
      {!revealed && !failed && (
        <div className="cover">
          <img
            className="thumbnail"
            src={youTubeThumbnailUrl(videoId)}
            alt={t('songForm.youtubePreview')}
            onError={() => setRevealedId(videoId)}
            draggable={false}
          />
          <div className="actions">
            <button type="button" className="action" onClick={handlePlay}>
              <PlayIcon className="play-icon" />
              <span className="label">{t('songForm.youtubePlay')}</span>
            </button>
            {onPractice && (
              <button type="button" className="action" onClick={() => onPractice(videoId)}>
                <RepeatIcon />
                <span className="label">{t('songForm.youtubePractice')}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

YouTubePreview.propTypes = {
  url: PropTypes.string,
  onPractice: PropTypes.func,
  className: PropTypes.string,
};

export default YouTubePreview;
